import type { SongControler } from "@/lib/track/controler/song-controler";
import type { Lane } from "@/lib/track/lane";
import type { LocalTransport, TransportBound } from "@/lib/track/local-transport";
import type { Track } from "@/lib/track/track";
import type { Transport } from "@/lib/track/transport";
import { buildLaneView, type LaneView } from "../lane-view";
import { Observable, type ReadonlyObservable } from "../observable";
import type { OffstageSong, SequenceLayout, SongBoundary } from "../view/sequence-layout";

/**
 * La séquence de morceaux du studio : plusieurs `SongControler`, à la suite,
 * dont un seul est sous la tête de lecture.
 *
 * # Pourquoi « séquence »
 *
 * Le domaine offrait trois mots, et deux étaient déjà pris. « Programme »
 * est le programme pédagogique — `/learn`, les exercices, la phase
 * `programme` de la fiche du morceau — et une liste de morceaux à jouer sous
 * le même nom aurait fait deux programmes dans le même studio. « Liste de
 * lecture » est la playlist de la bibliothèque, une collection dans laquelle
 * on choisit, pas ce qui est posé sur le rouleau. Reste « séquence » : des
 * morceaux dans un ordre, et rien d'autre. Un enregistrement de récital en
 * fait une de ses prises, un medley de ses chansons, une pièce de ses
 * mouvements, une séance de travail des morceaux qu'on enchaîne.
 *
 * # L'invariant : un morceau, une analyse, des ticks depuis zéro
 *
 * Les morceaux ne sont **pas** concaténés sur un axe de ticks commun. Chacun
 * garde sa grille, son tempo, ses mesures numérotées depuis 1, son analyse
 * — et le transport est toujours dans les ticks du morceau courant, avec la
 * grille de celui-ci. C'est ce qui laisse intact tout ce qui lit l'analyse
 * contre la position de la tête : les accords, la tonalité, les sections,
 * les conseils, les parcours lisent « le courant » et n'ont rien à savoir
 * de la séquence. Ce qui met les morceaux bout à bout est un **décalage
 * d'affichage** : le rouleau et la minimap accumulent les durées en ticks
 * (`offsetTicks`), et dessinent les morceaux hors scène à leur place. Voir
 * `SequenceLayout`.
 *
 * Le prix de ce choix, assumé : les morceaux hors scène se dessinent à
 * l'échelle du morceau courant. Deux morceaux de même grille — les prises
 * d'un même enregistrement, le premier client — se suivent sans couture ;
 * deux morceaux de tempo ou de PPQ différents changent d'échelle à l'instant
 * où la tête franchit la frontière, comme le rouleau change d'échelle quand
 * on charge un autre morceau.
 *
 * # Le morceau courant se lit, il ne se pose pas
 *
 * La position de lecture dans une séquence est un couple : le morceau, et le
 * tick dans ce morceau. Le transport porte le tick ; la séquence porte le
 * morceau, et le publie (`current`). La page du studio ne tient plus le
 * contrôleur chargé comme un état posé au chargement : elle lit le courant,
 * et tout ce qu'elle en dérive suit la tête quand celle-ci change de
 * morceau.
 *
 * # Un seul morceau en scène
 *
 * Seul le courant est monté en piste dans la session — ordonnancé, présent
 * dans le gestionnaire de pistes, trouvé par tout ce qui y cherche « le
 * morceau chargé ». Les autres sont **hors scène** : chargés, leurs lanes
 * intactes, sans piste. Ils se dessinent par la mise en page de la séquence
 * et ne sonnent pas, sans qu'on ait rien à couper. Quand la tête passe dans
 * l'un d'eux, il monte en piste et le précédent en redescend sans être
 * détruit (`Track.release`) : ses notes et son synthé l'attendent.
 *
 * # Aux frontières : la lecture continue
 *
 * Arriver au bout d'un morceau amène au suivant, au tick 0 exactement, et la
 * lecture continue. Une séquence existe pour être traversée — un
 * enregistrement de prises, un medley —, et s'arrêter à chaque frontière
 * demanderait un geste par morceau, ce qui est le contraire de ce qu'elle
 * promet. Pas de décompte non plus, à la différence du retour de boucle : le
 * décompte sert à rentrer *sur* une pulsation, et la traversée ne quitte pas
 * la sienne. Le bout du **dernier** morceau ne change rien à ce qui se
 * passait sans séquence : la tête continue dans le vide, comme depuis
 * toujours. L'exactitude du tick 0 compte : un ordonnanceur reprend « au
 * premier événement dont le tick est au moins la position », et repartir de
 * quelques ticks de dépassement aurait sauté la première note de chaque
 * prise.
 *
 * Le franchissement lui-même est tenu par la borne du transport
 * (`LocalTransport.setBound`) : c'est lui qui constate la fin en lecture, ou
 * un déplacement sorti du morceau, et qui passe la main ici *avant* de
 * distribuer quoi que ce soit — voir là-bas pourquoi le faire d'ici, après
 * coup, faisait sauter ou rejouer des notes. Vers l'arrière aussi : la
 * minimap, la molette et le glissé remontent dans le morceau précédent, le
 * plancher de la vue étant descendu au début de la séquence
 * (`ViewNavigator.setFloorTick`).
 *
 * # Une boucle ne déborde pas du morceau
 *
 * Une boucle est posée en ticks d'un morceau, par ses mesures. Elle tourne
 * dans ce morceau : la tête revient à son début avant d'en atteindre la fin,
 * donc ne traverse jamais. Et un changement de morceau — par un déplacement,
 * par le sélecteur — la retire : gardée, elle aurait borné le suivant à des
 * ticks qui ne veulent rien dire chez lui.
 *
 * # Ce que la séquence ne fait pas
 *
 * Elle ne place pas la tête quand on la remplit : `set` monte le premier
 * morceau et le rend courant, mais laisse la tête où elle est, comme
 * `addTrack` le faisait — c'est l'appelant qui sait s'il veut le début, la
 * première note, ou l'endroit où l'on était. Elle ne se compose pas avec une
 * séance live : la grille de séance et celle du morceau se disputeraient le
 * transport, et un changement de morceau monterait une piste dans la
 * session de tout le monde. Tant que la session qui tient ses pistes n'est
 * pas en scène, elle est suspendue : plus de borne, plus de traversée (voir
 * `setSuspended`).
 */

/**
 * Ce que le studio prête à la séquence : de quoi monter et démonter des
 * pistes, et le transport à borner. Une seule direction, comme
 * `SessionStage` — la séquence ne connaît de la session que ces verbes.
 */
export interface SequenceStage {
  readonly transport: Transport;
  readonly localTransport: LocalTransport;
  /**
   * La piste de ce morceau s'il est déjà en scène — monté par quelqu'un
   * d'autre, comme le panneau de génération le fait avant de prévenir la
   * page —, sinon `null`. La séquence reprend cette piste-là plutôt que d'en
   * monter une seconde sur les mêmes lanes.
   */
  staged(controler: SongControler): Track | null;
  /** Monte le morceau en piste : ordonnancé, présenté. */
  mount(controler: SongControler): Track;
  /** Sort la piste de scène en gardant ses lanes. Voir `StudioSession.releaseTrack`. */
  release(track: Track): void;
  /** Sort la piste de scène et détruit tout : lanes, synthés, contrôleur. */
  remove(track: Track): void;
  /** Ce que la vue dessine de la séquence a changé : décalages, frontières, plancher. */
  changed(): void;
}

/** Un morceau de la séquence, tel qu'on le donne : le contrôleur, et ce qu'on écrit à sa frontière. */
export interface SequenceSong {
  readonly controler: SongControler;
  /** « Prise 3 », « Allegro ». À défaut, le titre du morceau, puis « Morceau n ». */
  readonly label?: string;
}

/** Un morceau de la séquence, tel qu'on le lit : avec sa place sur l'axe global. */
export interface SequenceEntry {
  readonly controler: SongControler;
  readonly label: string;
  /** Où tombe son tick 0 sur l'axe global, en ticks : la somme des durées qui le précèdent. */
  readonly offsetTicks: number;
  readonly durationTicks: number;
}

/** Ce que la séquence tient d'un morceau, en plus de ce qu'elle en montre. */
interface Entry {
  readonly controler: SongControler;
  readonly label: string | null;
  /** Sa piste, quand il est en scène. `null` hors scène. */
  track: Track | null;
}

const NO_SONGS: readonly OffstageSong[] = Object.freeze([]);
const NO_BOUNDARIES: readonly SongBoundary[] = Object.freeze([]);

export class SongSequence implements SequenceLayout {
  private entries: Entry[] = [];
  /** L'indice du morceau sous la tête, `-1` quand la séquence est vide. */
  private index = -1;
  /** Voir `setSuspended`. */
  private suspended = false;

  private readonly _current = new Observable<SongControler | null>(null);
  private _version = 0;

  // Ce que la vue lit à chaque image, calculé une fois par version : les
  // décalages ne bougent qu'à un ajout, un retrait ou un changement de
  // morceau, et une liste reconstruite par image aurait coûté sur le cas où
  // elle est vide.
  private offsets: number[] = [];
  private offstageSongs: readonly OffstageSong[] = NO_SONGS;
  private boundaryList: readonly SongBoundary[] = NO_BOUNDARIES;
  private laneViews = new Map<Lane, LaneView>();

  private readonly unsubscribeBound: () => void;

  constructor(private readonly stage: SequenceStage) {
    this.unsubscribeBound = stage.localTransport.on((event) => {
      if (event.type === "bound") {
        this.onBound(event.tick);
      }
    });
  }

  // ── Lecture ──────────────────────────────────────────────────

  /** Le morceau sous la tête de lecture, `null` quand rien n'est chargé. */
  get current(): ReadonlyObservable<SongControler | null> {
    return this._current;
  }

  get currentIndex(): number {
    return this.index;
  }

  get length(): number {
    return this.entries.length;
  }

  /** Les morceaux, dans l'ordre, avec leur place sur l'axe global. */
  getEntries(): readonly SequenceEntry[] {
    return this.entries.map((entry, i) => ({
      controler: entry.controler,
      label: this.labelOf(i),
      offsetTicks: this.offsets[i],
      durationTicks: this.durationOf(entry),
    }));
  }

  indexOf(controler: SongControler): number {
    return this.entries.findIndex((entry) => entry.controler === controler);
  }

  /** La durée de toute la séquence, en ticks accumulés. */
  get totalTicks(): number {
    if (this.entries.length === 0) {
      return 0;
    }

    const last = this.entries.length - 1;

    return this.offsets[last] + this.durationOf(this.entries[last]);
  }

  /** Un tick du morceau courant, sur l'axe global. */
  positionOf(localTick: number): number {
    return this.currentOffset + localTick;
  }

  /**
   * Où tombe un tick de l'axe global : dans quel morceau, et à quel tick de
   * celui-ci. Avant le début, c'est le début du premier ; au-delà de la fin,
   * c'est le dernier, au-delà de sa durée — là où la tête irait aussi sans
   * séquence.
   */
  locate(globalTick: number): { index: number; localTick: number } {
    if (this.entries.length === 0) {
      return { index: -1, localTick: Math.max(0, globalTick) };
    }

    if (globalTick < 0) {
      return { index: 0, localTick: 0 };
    }

    let index = this.entries.length - 1;

    for (let i = 0; i < this.entries.length; i++) {
      if (globalTick < this.offsets[i] + this.durationOf(this.entries[i])) {
        index = i;
        break;
      }
    }

    return { index, localTick: globalTick - this.offsets[index] };
  }

  // ── Mise en page (SequenceLayout) ────────────────────────────

  get version(): number {
    return this._version;
  }

  get currentOffset(): number {
    return this.index >= 0 ? this.offsets[this.index] : 0;
  }

  offstage(): readonly OffstageSong[] {
    return this.offstageSongs;
  }

  boundaries(): readonly SongBoundary[] {
    return this.boundaryList;
  }

  /**
   * La vue d'une lane d'un morceau de la séquence — sa couleur de main,
   * surtout —, ou `undefined` si la lane n'est à aucun d'eux. La session ne
   * connaît que les lanes en scène ; celles des morceaux hors scène n'ont
   * qu'ici pour dire de quelle main elles sont.
   */
  laneViewOf(lane: Lane): LaneView | undefined {
    return this.laneViews.get(lane);
  }

  // ── Remplir ──────────────────────────────────────────────────

  /**
   * Remplace la séquence par ces morceaux.
   *
   * Ceux qui n'y sont plus sont détruits ; ceux qui y restent gardent leur
   * état, et le courant reste courant s'il est encore là — sinon c'est le
   * premier. La tête n'est pas déplacée : voir la note de tête.
   */
  set(songs: readonly (SequenceSong | SongControler)[]): void {
    const next: Entry[] = [];

    // Un morceau ne figure qu'une fois : deux entrées sur les mêmes lanes se
    // dessineraient l'une sur l'autre.
    for (const song of songs) {
      const entry = this.entryFor(song);

      if (!next.some((candidate) => candidate.controler === entry.controler)) {
        next.push(entry);
      }
    }

    const kept = new Set(next.map((entry) => entry.controler));
    const previous = this.index >= 0 ? this.entries[this.index] : null;

    for (const entry of this.entries) {
      if (!kept.has(entry.controler)) {
        this.discardEntry(entry);
      }
    }

    this.entries = next;

    const keptIndex = previous === null ? -1 : next.indexOf(previous);
    this.enter(keptIndex >= 0 ? keptIndex : (next.length > 0 ? 0 : -1), previous);
  }

  /**
   * Change l'étiquette d'un morceau, et rien d'autre : ni le courant, ni la
   * tête. `set` ne convient pas — il recrée l'entrée dont l'étiquette change,
   * et la séquence, qui retrouve le courant par son entrée, repartirait du
   * premier morceau. C'est ce que faisait nommer une prise pendant qu'on
   * l'écoute (2026-09-25).
   */
  relabel(controler: SongControler, label: string): void {
    const index = this.indexOf(controler);

    if (index < 0 || this.entries[index].label === label) {
      return;
    }

    this.entries[index] = { ...this.entries[index], label };
    this.rebuildLayout();
    this._version += 1;
    this.stage.changed();
  }

  /** Un seul morceau, comme le studio en a toujours chargé un. */
  replace(controler: SongControler, label?: string): void {
    this.set([{ controler, label }]);
  }

  /**
   * Ajoute un morceau à la fin. Il devient courant s'il est le premier ;
   * sinon il attend hors scène que la tête arrive.
   */
  append(controler: SongControler, label?: string): void {
    if (this.indexOf(controler) >= 0) {
      return;
    }

    this.entries.push(this.entryFor({ controler, label }));

    if (this.index < 0) {
      this.enter(0, null);

      return;
    }

    this.ensureOffstage(this.entries[this.entries.length - 1]);
    this.settle();
  }

  /**
   * Retire un morceau et le détruit. Si c'était le courant, celui qui prend
   * sa place — le suivant, ou le dernier — devient courant, à son début.
   */
  remove(controler: SongControler): void {
    const at = this.indexOf(controler);

    if (at < 0) {
      return;
    }

    this.discardEntry(this.entries[at]);
    this.dropAt(at);
  }

  /**
   * Sort un morceau de la séquence **sans le détruire** : sa piste redescend,
   * ses lanes restent. C'est à l'appelant de le rendre — `replace`, `append`
   * — ou de le jeter (`discard`). La page s'en sert pour le « retour » au
   * morceau d'avant un exercice : il doit survivre à son remplacement.
   */
  withdraw(controler: SongControler): void {
    const at = this.indexOf(controler);

    if (at < 0) {
      return;
    }

    this.ensureOffstage(this.entries[at]);
    this.dropAt(at);
  }

  /**
   * Détruit un morceau — lanes, synthés, contrôleur —, qu'il soit encore
   * dans la séquence ou qu'on l'en ait retiré par `withdraw`.
   */
  discard(controler: SongControler): void {
    if (this.indexOf(controler) >= 0) {
      this.remove(controler);

      return;
    }

    this.discardEntry({ controler, label: null, track: null });
  }

  clear(): void {
    for (const entry of this.entries) {
      this.discardEntry(entry);
    }

    this.entries = [];
    this.enter(-1, null);
  }

  // ── Se déplacer ──────────────────────────────────────────────

  /** Place la tête au tick `localTick` de ce morceau, qui devient courant. */
  goTo(index: number, localTick = 0): void {
    if (index < 0 || index >= this.entries.length) {
      return;
    }

    this.switchTo(index, localTick);
  }

  next(): void {
    this.goTo(this.index + 1);
  }

  previous(): void {
    this.goTo(this.index - 1);
  }

  /**
   * Suspend la séquence, ou la reprend.
   *
   * Suspendue, elle ne borne plus le transport et ne change plus de morceau :
   * c'est l'état où la session qui tient ses pistes n'est pas en scène — une
   * séance live a pris le transport, et un franchissement monterait un
   * morceau dans la session de tout le monde. Ce qu'elle tient reste tel
   * quel, et revient avec la session.
   */
  setSuspended(suspended: boolean): void {
    if (suspended === this.suspended) {
      return;
    }

    this.suspended = suspended;
    this.applyBound();
  }

  dispose(): void {
    this.unsubscribeBound();

    // Ce qui est en scène appartient à la session, qui le détruit avec elle ;
    // ce qui est hors scène n'a qu'ici pour être détruit.
    for (const entry of this.entries) {
      if (entry.track === null) {
        this.destroyOffstage(entry.controler);
      }
    }

    this.entries = [];
    this.index = -1;
    this.stage.localTransport.setBound(null);
    this._current.dispose();
  }

  // ── Interne ──────────────────────────────────────────────────

  private entryFor(song: SequenceSong | SongControler): Entry {
    const controler = "controler" in song ? song.controler : song;
    const label = "controler" in song ? (song.label ?? null) : null;
    const existing = this.entries.find((entry) => entry.controler === controler);

    if (existing === undefined) {
      return { controler, label, track: null };
    }

    // Un morceau déjà là garde sa piste ; une étiquette redonnée, elle, vaut.
    return label === null || label === existing.label
      ? existing
      : { controler, label, track: existing.track };
  }

  private labelOf(index: number): string {
    const entry = this.entries[index];

    if (entry.label !== null) {
      return entry.label;
    }

    const title = entry.controler.getSong().getTitle();

    return title !== null && title !== "" ? title : `Morceau ${index + 1}`;
  }

  private durationOf(entry: Entry): number {
    return entry.controler.getSong().getDurationTicks();
  }

  /**
   * Le tick de l'axe du rouleau reçu du transport : hors du morceau courant,
   * par la lecture ou par un déplacement. Ramené sur l'axe global, puis dans
   * le morceau où il tombe.
   */
  private onBound(tick: number): void {
    if (this.suspended || this.index < 0) {
      return;
    }

    const { index, localTick } = this.locate(this.positionOf(tick));

    if (index < 0) {
      return;
    }

    this.switchTo(index, localTick);
  }

  /** Rend ce morceau courant et y place la tête. */
  private switchTo(index: number, localTick: number): void {
    if (index !== this.index) {
      if (this.index >= 0) {
        this.ensureOffstage(this.entries[this.index]);
      }

      this.index = index;
      // La grille du transport est celle du morceau courant : `load()` avait
      // posé celle du dernier morceau chargé, qui n'est pas forcément lui.
      this.stage.transport.setMusicalTimeline(this.entries[index].controler.getSong().getMusicalTimeline());
      // Une boucle est posée dans les ticks d'un morceau ; voir la note de tête.
      this.stage.localTransport.setLoop(null);
      this.ensureStaged(this.entries[index]);
    }

    // La borne avant le déplacement : posée après, un tick 0 avec un morceau
    // devant serait encore lu comme « avant le début » de l'ancien.
    this.settle();
    this.stage.localTransport.seekToTick(localTick);
  }

  /**
   * Rend courant le morceau à cet indice sans déplacer la tête, ou vide la
   * scène avec `-1`. Le point commun de `set`, `clear` et du retrait du
   * courant. `previous` est le morceau qui était courant, s'il l'est encore
   * : son état — sa boucle, en particulier — n'a pas à repartir.
   */
  private enter(index: number, previous: Entry | null): void {
    this.index = index;

    for (const entry of this.entries) {
      if (entry !== this.entries[index]) {
        this.ensureOffstage(entry);
      }
    }

    if (index >= 0) {
      const entry = this.entries[index];

      // Toujours reposée, même pour un courant qui le reste : `load()` pose
      // la grille du morceau qu'il charge, et un morceau ajouté derrière lui
      // a pu remplacer la sienne sur le transport entre-temps. Le même objet
      // reposé ne change rien pour qui le tient déjà.
      this.stage.transport.setMusicalTimeline(entry.controler.getSong().getMusicalTimeline());

      if (entry !== previous) {
        this.stage.localTransport.setLoop(null);
      }

      this.ensureStaged(entry);
    }

    this.settle();
  }

  /** Retire l'entrée à cet indice, déjà démontée, et replace la tête s'il le faut. */
  private dropAt(at: number): void {
    const wasCurrent = at === this.index;
    this.entries.splice(at, 1);

    if (this.entries.length === 0) {
      this.enter(-1, null);

      return;
    }

    if (wasCurrent) {
      this.enter(Math.min(at, this.entries.length - 1), null);
      this.stage.localTransport.seekToTick(0);

      return;
    }

    if (at < this.index) {
      this.index -= 1;
    }

    this.settle();
  }

  private ensureStaged(entry: Entry): void {
    if (entry.track !== null) {
      return;
    }

    entry.track = this.stage.staged(entry.controler) ?? this.stage.mount(entry.controler);
  }

  private ensureOffstage(entry: Entry): void {
    const track = entry.track ?? this.stage.staged(entry.controler);
    entry.track = null;

    if (track === null) {
      return;
    }

    this.stage.release(track);

    // Un morceau redescendu de scène reprend toutes ses voix. Ce qu'on avait
    // coupé ou caché était un choix d'écoute sur le morceau *courant*, et la
    // barre d'écoute repart à « Deux » dès que le courant change : elle
    // annoncerait deux mains sur un morceau revenu à une seule. Pire, une
    // piste remontée dont la première lane arrive coupée se coupe entière
    // (`Track.addLane`), sans que rien à l'écran le dise. Après le
    // relâchement, pour que la piste qui s'en va n'en entende rien.
    for (const lane of entry.controler.getLanes()) {
      lane.unmute();
      lane.show();
    }
  }

  private discardEntry(entry: Entry): void {
    const track = entry.track ?? this.stage.staged(entry.controler);
    entry.track = null;

    if (track !== null) {
      this.stage.remove(track);

      return;
    }

    this.destroyOffstage(entry.controler);
  }

  /** Un morceau sans piste n'a personne d'autre pour détruire ses lanes. */
  private destroyOffstage(controler: SongControler): void {
    for (const lane of controler.getLanes()) {
      lane.destroy();
    }

    controler.destroy();
  }

  /**
   * Recalcule ce qui dépend de l'ordre et du courant — décalages, mise en
   * page, borne —, puis le dit à la vue et à la page.
   */
  private settle(): void {
    this.offsets = [];
    let offset = 0;

    for (const entry of this.entries) {
      this.offsets.push(offset);
      offset += this.durationOf(entry);
    }

    this.rebuildLayout();
    this.applyBound();
    this._version += 1;
    this.stage.changed();
    this._current.set(this.index >= 0 ? this.entries[this.index].controler : null);
  }

  private rebuildLayout(): void {
    const laneViews = new Map<Lane, LaneView>();

    for (const entry of this.entries) {
      for (const lane of entry.controler.getLanes()) {
        laneViews.set(lane, buildLaneView(lane, entry.controler));
      }
    }

    this.laneViews = laneViews;

    if (this.entries.length < 2) {
      this.offstageSongs = NO_SONGS;
      this.boundaryList = NO_BOUNDARIES;

      return;
    }

    const currentOffset = this.currentOffset;
    const offstage: OffstageSong[] = [];
    const boundaries: SongBoundary[] = [];

    for (let i = 0; i < this.entries.length; i++) {
      const offsetTicks = this.offsets[i] - currentOffset;
      boundaries.push({ tick: offsetTicks, label: this.labelOf(i) });

      if (i !== this.index) {
        offstage.push({ lanes: this.entries[i].controler.getLanes(), offsetTicks });
      }
    }

    this.offstageSongs = offstage;
    this.boundaryList = boundaries;
  }

  /** La borne du transport pour le morceau courant, ou rien s'il n'a rien à côté. */
  private applyBound(): void {
    this.stage.localTransport.setBound(this.transportBound());
  }

  private transportBound(): TransportBound | null {
    if (this.suspended || this.index < 0 || this.entries.length < 2) {
      return null;
    }

    const last = this.entries.length - 1;

    return {
      before: this.index > 0,
      endTick: this.index < last ? this.durationOf(this.entries[this.index]) : null,
    };
  }
}
